import config from "../../common/Config";
import {
  encryptPayload,
  normalizeEncryptedApiResponse,
} from "../../../utils/customerCrypto";
import {
  getCompanySettings,
  resolveCustomerApiEncrypt,
} from "../../../utils/companySettingsCache";

export const CUSTOMER_FORM_FIELD_ALIASES = {
  business_name: "companyName",
  company_name: "companyName",
  company: "companyName",
  customer_name: "firstName",
  customername: "firstName",
  contact_name: "firstName",
  contactname: "firstName",
  first_name: "firstName",
  firstname: "firstName",
  full_name: "firstName",
  fullname: "firstName",
  name: "firstName",
  last_name: "lastName",
  lastname: "lastName",
  businessname: "companyName",
  companyname: "companyName",
  email: "emailId",
  email_address: "emailId",
  email_id: "emailId",
  emailaddress: "emailId",
  phone_number: "phone",
  mobile: "phone",
  account_no: "accound_no",
  account_number: "accound_no",
  accountnumber: "accound_no",
  username: "accound_no",
  user_name: "accound_no",
  route_id: "route",
  type_of_business: "typeOfBusiness",
  business_type: "typeOfBusiness",
  typeofbusiness: "typeOfBusiness",
  business_category: "businessCategory",
  business_category_id: "businessCategory",
  category_id: "businessCategory",
  delivery_days: "default_delivery",
  deliverydays: "default_delivery",
  default_delivery_days: "default_delivery",
  address: "addressLine1",
  address_line1: "addressLine1",
  address_line_1: "addressLine1",
  address1: "addressLine1",
  address2: "addressLine2",
  address_line2: "addressLine2",
  address_line_2: "addressLine2",
  state: "county",
  postal_code: "postalCode",
  postcode: "postalCode",
  zip: "postalCode",
  vat_no: "vatNo",
  vat_number: "vatNo",
  vatnumber: "vatNo",
  vat: "vatNo",
  gst_no: "vatNo",
  gst_number: "vatNo",
  tax_number: "vatNo",
  is_po: "isPo",
  po_required: "isPo",
  acceptTerms: "accept_terms",
  terms: "accept_terms",
  ship_to_name: "shiptoName",
  shipto_name: "shiptoName",
  ship_name: "shiptoName",
  ship_line1: "shipLine1",
  ship_address1: "shipLine1",
  ship_address_line1: "shipLine1",
  ship_line2: "shipLine2",
  ship_address2: "shipLine2",
  ship_address_line2: "shipLine2",
  ship_city: "shipCity",
  ship_postcode: "shipPostcode",
  ship_postal_code: "shipPostcode",
  ship_state: "shipState",
  ship_county: "shipState",
  qty_discount: "qtyDiscount",
  price_group: "product_price",
  pricegroup: "product_price",
  priceGroup: "product_price",
  productprice: "product_price",
  product_price_type: "product_price",
  productPrice: "product_price",
};

export const resolveCustomerFormFieldName = (apiName) => {
  const raw = String(apiName || "").trim();
  if (!raw) return "";
  if (CUSTOMER_FORM_FIELD_ALIASES[raw]) return CUSTOMER_FORM_FIELD_ALIASES[raw];
  const lower = raw.toLowerCase();
  if (CUSTOMER_FORM_FIELD_ALIASES[lower]) return CUSTOMER_FORM_FIELD_ALIASES[lower];
  const snake = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
  if (CUSTOMER_FORM_FIELD_ALIASES[snake]) return CUSTOMER_FORM_FIELD_ALIASES[snake];
  const compact = snake.replace(/_/g, "");
  if (CUSTOMER_FORM_FIELD_ALIASES[compact]) return CUSTOMER_FORM_FIELD_ALIASES[compact];
  return raw;
};

export const isSectionVisible = (section, formData = {}) => {
  const rule = section?.visibleWhen;
  if (!rule || !rule.field) {
    return true;
  }
  const formKey = resolveCustomerFormFieldName(rule.field);
  const current = formData[formKey];
  const values = Array.isArray(rule.values) ? rule.values : [];
  return values.map(String).includes(String(current));
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeOption = (option, field = {}) => {
  if (option == null) return null;
  if (typeof option !== "object") {
    return { label: String(option), value: String(option) };
  }
  const valueKey = field.optionValue || "value";
  const labelKey = field.optionLabel || "label";
  const value = option[valueKey] ?? option.value ?? option.id ?? option.key ?? "";
  const label =
    option[labelKey] ?? option.label ?? option.name ?? option.title ?? value;
  return { label: String(label), value: String(value) };
};

const normalizeField = (field = {}) => {
  const name = String(field.name || field.field_name || field.key || "").trim();
  let options = asArray(field.options || field.values || field.choices)
    .map((option) => normalizeOption(option, field))
    .filter(Boolean);

  let defaultValue =
    field.default ?? field.defaultValue ?? field.default_value ?? "";
  if (typeof defaultValue === "boolean") {
    defaultValue = defaultValue ? "1" : "0";
  }

  const spanColumns = Number(field.columns || 0);
  const column = Number(field.column || field.col || 0) || 0;

  return {
    name,
    label: field.label || field.field_label || field.title || "",
    type: String(field.type || field.field_type || "text").toLowerCase(),
    required: field.required === true || field.required === 1 || field.required === "1",
    placeholder: field.placeholder || field.place_holder || "",
    column,
    spanColumns,
    options,
    optionsSource: field.optionsSource || field.options_source || "",
    optionValue: field.optionValue || "value",
    optionLabel: field.optionLabel || "label",
    defaultValue,
    visible: field.visible !== false && field.visible !== 0 && field.visible !== "0",
    readOnly: field.readOnly === true || field.readonly === true || field.read_only === true,
    maxLength: field.maxLength ?? field.max_length ?? field.maxlength ?? null,
    min: field.min,
    max: field.max,
    step: field.step,
    accept: asArray(field.accept),
    multiple: field.multiple === true,
    fields: asArray(
      (field.fields && field.fields.length ? field.fields : field.sub_fields) ||
        field.subFields
    ).map(normalizeField),
    minItems: field.minItems,
    maxItems: field.maxItems,
    emptyMessage: field.emptyMessage || "",
  };
};

export const normalizeDynamicFormResponse = (raw) => {
  const root =
    raw?.form && typeof raw.form === "object"
      ? raw.form
      : raw?.data?.form && typeof raw.data.form === "object"
        ? raw.data.form
        : raw?.data && typeof raw.data === "object"
          ? raw.data
          : raw ?? {};

  const sections = asArray(
    root.sections || root.form_sections || root.groups || []
  ).map((section, index) => ({
    id: section.id ?? section.key ?? `section_${index}`,
    title: section.title || section.label || "",
    layout: section.layout || {},
    visibleWhen: section.visibleWhen || section.visible_when || null,
    fields: asArray(section.fields || section.form_fields || [])
      .map(normalizeField)
      .filter((field) => field.name),
  }));

  let fields = asArray(
    root.fields || root.form_fields || root.formFields || []
  )
    .map(normalizeField)
    .filter((field) => field.name);

  if (!fields.length && sections.length) {
    fields = sections.flatMap((section) => section.fields);
  }

  return {
    id: root.id || "",
    title: root.title || root.form_title || "",
    submitLabel: root.submitLabel || root.submit_label || "",
    submitEndpoint: root.submitEndpoint || root.submit_endpoint || "",
    method: root.method || "POST",
    sections,
    fields,
  };
};

export const resolveOptionsSourceKey = (optionsSource = "") => {
  const raw = String(optionsSource || "").toLowerCase();
  if (!raw) return "";
  const segmentMatch = raw.match(/form-options\/(?:product|customer)\/([^/?#]+)/i);
  const segment = segmentMatch ? segmentMatch[1] : raw.split("/").filter(Boolean).pop() || raw;
  if (segment.includes("route") || raw.includes("route")) return "routes";
  if (
    segment.includes("price-group") ||
    segment.includes("price_group") ||
    segment.includes("pricegroup") ||
    raw.includes("price-group") ||
    raw.includes("price_group")
  ) {
    return "priceGroups";
  }
  if (
    segment.includes("business_type") ||
    segment.includes("typeofbusiness") ||
    raw.includes("business_type") ||
    raw.includes("typeofbusiness") ||
    raw.includes("type_of_business")
  ) {
    return "businessTypes";
  }
  if (segment.includes("business_categor") || segment.includes("categor") || raw.includes("categor")) {
    return "businessCategories";
  }
  if (segment.includes("deliver") || raw.includes("deliver")) return "deliveryDays";
  return segment || raw;
};

export const extractFormOptionsSegment = (optionsSource = "") => {
  const raw = String(optionsSource || "").trim();
  if (!raw) return "";
  const match = raw.match(/form-options\/(?:product|customer)\/([^/?#]+)/i);
  if (match?.[1]) return match[1];
  const parts = raw.replace(/^\/+|\/+$/g, "").split("/");
  return parts[parts.length - 1] || "";
};

const normalizeFormOptionItem = (option) => {
  if (option == null) return null;
  if (typeof option !== "object") {
    return { label: String(option), value: String(option) };
  }
  const value = option.value ?? option.id ?? option.key ?? option.code ?? "";
  const label =
    option.label ??
    option.name ??
    option.title ??
    option.route_number ??
    option.type_business ??
    option.category_name ??
    value;
  if (value === "" && label === "") return null;
  return { ...option, label: String(label), value: String(value) };
};

export const normalizeFormOptionsResponse = (raw) => {
  const root = raw?.data ?? raw?.options ?? raw?.items ?? raw?.results ?? raw ?? {};
  const list = Array.isArray(root)
    ? root
    : Array.isArray(root.data)
      ? root.data
      : Array.isArray(root.options)
        ? root.options
        : Array.isArray(root.items)
          ? root.items
          : [];
  return list.map(normalizeFormOptionItem).filter(Boolean);
};

export const fetchCustomerFormOptions = async ({
  segment,
  authToken,
  method = "POST",
} = {}) => {
  const token =
    authToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken");
  if (!segment) return [];
  const settings = await getCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const headers = { Authorization: `Bearer ${token}` };
  const url = `${config.API_URL}/form-options/customer/${encodeURIComponent(segment)}`;

  let response;
  if (String(method).toUpperCase() === "GET") {
    response = await fetch(url, { method: "GET", headers });
  } else {
    let body;
    if (useEncrypt) {
      const encryptedPayload = encryptPayload({ segment, module: "customer" });
      const formData = new FormData();
      if (encryptedPayload) formData.append("encrypted_payload", encryptedPayload);
      body = formData;
    } else {
      const formData = new FormData();
      formData.append("segment", segment);
      formData.append("module", "customer");
      body = formData;
    }
    response = await fetch(url, { method: "POST", headers, body });
  }

  const json = await response.json().catch(() => ({}));
  const resolved = normalizeEncryptedApiResponse(json, useEncrypt);
  if (!response.ok && resolved?.success !== true) {
    throw new Error(resolved?.message || `Failed to load customer options: ${segment}`);
  }
  return normalizeFormOptionsResponse(resolved);
};

export const collectFormOptionsSources = (fields = []) => {
  const sources = new Set();
  const walk = (list) => {
    (list || []).forEach((field) => {
      if (field?.optionsSource) sources.add(String(field.optionsSource));
      if (field?.fields?.length) walk(field.fields);
    });
  };
  walk(fields);
  return [...sources];
};

export const loadCustomerFormOptionsMap = async ({
  fields = [],
  authToken,
} = {}) => {
  const sources = collectFormOptionsSources(fields);
  const map = {};
  await Promise.all(
    sources.map(async (source) => {
      const segment = extractFormOptionsSegment(source);
      if (!segment) return;
      try {
        const options = await fetchCustomerFormOptions({ segment, authToken });
        const key = resolveOptionsSourceKey(source);
        map[key] = options;
        map[segment] = options;
        map[source] = options;
        map[`/api/form-options/customer/${segment}`] = options;
        map[`form-options/customer/${segment}`] = options;
      } catch {
      }
    })
  );
  return map;
};

export const fetchCustomerDynamicForm = async ({
  formKey = "add_customer",
  authToken,
} = {}) => {
  const token =
    localStorage.getItem("token") ||
    authToken ||
    localStorage.getItem("accessToken");
  if (!token) {
    throw new Error("Auth token missing.");
  }
  const settings = await getCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const params = new URLSearchParams({
    form: formKey,
    form_name: formKey,
    form_key: formKey,
    form_id: formKey,
    module: "customers",
    type: formKey,
    platform: "web",
  });
  const url = `${config.API_URL}/get_dynamic_form_customer?${params.toString()}`;
  const headers = {
    authorization: `Bearer ${token}`,
    Accept: "application/json",
  };

  const getForm = async (encrypt) => {
    const response = await fetch(url, {
      method: "GET",
      headers,
    });
    const json = await response.json().catch(() => ({}));
    const resolved = normalizeEncryptedApiResponse(json, encrypt);
    return { response, resolved };
  };

  let { response, resolved } = await getForm(useEncrypt);

  if (response.status === 401 && useEncrypt) {
    ({ response, resolved } = await getForm(false));
  }

  if (response.status === 401 && !useEncrypt) {
    ({ response, resolved } = await getForm(true));
  }

  if (!response.ok && resolved?.success !== true) {
    throw new Error(resolved?.message || "Failed to load customer dynamic form");
  }
  return normalizeDynamicFormResponse(resolved);
};
